// frontend/src/components/wizard/StepReview/NotificationPanel.jsx
import React, { useState } from 'react';
import { Mail, AlertCircle, X } from 'lucide-react';
import { useLanguageStore } from '../../../store/useLanguageStore';

/**
 * Input con chips de correos electrónicos.
 * Acepta múltiples emails separados por coma, punto y coma, espacio o pegar.
 *
 * BUG FIX: reemplaza alert() nativo por onLimitReached callback,
 * para que el parent (StepReview) pueda mostrar su Modal estándar.
 */
const EmailChipInput = ({ label, emails, setEmails, maxTotal, currentTotal, placeholder, onLimitReached }) => {
  const [inputValue, setInputValue] = useState('');
  const { t } = useLanguageStore();

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleAddEmails = (rawText) => {
    const extracted = rawText.split(/[\s,;]+/).map(e => e.trim()).filter(Boolean);
    const validNew = extracted.filter(e => isValidEmail(e) && !emails.includes(e));

    if (validNew.length === 0) {
      setInputValue('');
      return;
    }

    const available = maxTotal - currentTotal;
    const toAdd = validNew.slice(0, available);

    if (toAdd.length > 0) setEmails([...emails, ...toAdd]);

    // BUG FIX: antes usaba alert() — ahora llama al callback del parent
    if (validNew.length > available && onLimitReached) {
      onLimitReached(maxTotal);
    }

    setInputValue('');
  };

  return (
    <div className="flex flex-col mb-4">
      <label className="text-xs font-bold text-slate-600 mb-1.5 flex items-center">
        <span className="w-8 text-slate-400">{label}</span>
      </label>
      <div className="flex flex-wrap items-center gap-2 p-2 min-h-[42px] bg-white border border-slate-200 rounded-xl focus-within:border-indigo-500 focus-within:ring-1 focus-within:ring-indigo-500 transition-all">
        {emails.map((email, idx) => (
          <span
            key={idx}
            className="flex items-center px-2.5 py-1 rounded-md bg-slate-100 text-slate-700 text-xs font-medium border border-slate-200"
          >
            {email}
            <button
              type="button"
              onClick={() => setEmails(emails.filter(e => e !== email))}
              className="ml-1.5 text-slate-400 hover:text-red-500 focus:outline-none"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </span>
        ))}
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => {
            if (['Enter', 'Tab', ','].includes(e.key)) {
              e.preventDefault();
              handleAddEmails(inputValue);
            }
          }}
          onPaste={(e) => {
            e.preventDefault();
            handleAddEmails(e.clipboardData.getData('text'));
          }}
          onBlur={() => { if (inputValue.trim()) handleAddEmails(inputValue); }}
          placeholder={emails.length === 0 ? placeholder : ''}
          className="flex-1 bg-transparent border-none outline-none text-sm min-w-[150px] p-1 text-slate-700 placeholder-slate-400"
          disabled={currentTotal >= maxTotal}
        />
      </div>
    </div>
  );
};

export const NotificationPanel = ({
  notifications,
  setNotifications,
  MAX_EMAILS,
  currentTotalEmails,
  onLimitReached,       // BUG FIX: callback para mostrar Modal en lugar de alert()
}) => {
  const { t } = useLanguageStore();

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
      <h3 className="font-bold text-slate-800 flex items-center mb-6">
        <Mail className="w-5 h-5 mr-2 text-indigo-600" />
        {t('wizard.review.notifications.title') || 'Notificaciones y Alertas'}
      </h3>

      <div className="max-w-3xl">
        <EmailChipInput
          label="TO:"
          emails={notifications?.to || []}
          setEmails={(newTo) => setNotifications({ ...notifications, to: newTo })}
          maxTotal={MAX_EMAILS}
          currentTotal={currentTotalEmails}
          placeholder={t('wizard.review.notifications.to_ph') || 'Ej: admin@empresa.com (Presiona Enter)'}
          onLimitReached={onLimitReached}
        />
        <EmailChipInput
          label="CC:"
          emails={notifications?.cc || []}
          setEmails={(newCc) => setNotifications({ ...notifications, cc: newCc })}
          maxTotal={MAX_EMAILS}
          currentTotal={currentTotalEmails}
          placeholder={t('wizard.review.notifications.cc_ph') || 'Agregar correos en copia...'}
          onLimitReached={onLimitReached}
        />

        {(notifications?.to?.length || 0) === 0 && (notifications?.cc?.length || 0) === 0 && (
          <p className="text-red-500 text-xs font-bold flex items-center mt-2">
            <AlertCircle className="w-3.5 h-3.5 mr-1" />
            {t('wizard.review.notifications.empty_alert') || 'Debes incluir al menos un correo destinatario.'}
          </p>
        )}

        {currentTotalEmails >= MAX_EMAILS && (
          <p className="text-amber-600 text-xs font-bold flex items-center mt-2">
            <AlertCircle className="w-3.5 h-3.5 mr-1" />
            {t('wizard.review.notifications.limit_reached') || `Límite de ${MAX_EMAILS} destinatarios alcanzado.`}
          </p>
        )}
      </div>
    </div>
  );
};
